import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetStations } from '@ordr-as/ordr-service-types/epos';
import { useLocation } from '../../context/LocationContext';
import { useOrdrWebSocket } from '../../context/OrdrWebSocketProvider';
import { useOrdersWebSocketStore } from '../../context/OrdersWebSocketStore';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useScreenSize } from '../../context/ScreenSizeContext';
import { clampFocus, moveFocus } from './keyboardNav';
import type { StationSelection } from './StationDropdown';

/** One keyboard-navigable card. `action` present → Enter advances it; absent → Enter opens its detail. */
export type NavItem = { cardId: string; groupId: string; action?: () => void };

type PanelId = 'none' | 'recent';

const scrollCardIntoView = (cardId: string) => {
  const el = document.querySelector(`[data-kds-card][data-card-id="${CSS.escape(cardId)}"]`);
  el?.scrollIntoView({ block: 'nearest' });
};

/**
 * The KDS board engine: everything that is identical across the station and expo boards — chrome
 * (station list, the all-stations websocket subscription), keyboard focus, and the detail/aux panel
 * state machine. The two pages call this with their own nav contract and compose the result with
 * their own data; `navItems` is the only thing the modes shape differently (ADR 0004).
 */
export function useKdsBoard(navItems: NavItem[]) {
  const { locationId: locationIdParam } = useParams<{ locationId: string }>();
  const { location } = useLocation();
  const navigate = useNavigate();
  const { isDesktop } = useScreenSize();
  const { setStations } = useOrdrWebSocket();
  const connectionState = useOrdersWebSocketStore((s) => s.connectionState);
  const { data: stations, isPending: stationsPending } = useGetStations(location.id);

  // The board subscribes to every station: Expo spans them, and a station's "Waiting for X"
  // refreshes when an upstream station (a different board) finishes.
  useEffect(() => {
    const ids = (stations ?? []).map((s) => s.id).filter((id): id is number => id !== undefined);
    setStations(ids);
    return () => setStations([]);
  }, [stations, setStations]);

  const [openPanel, setOpenPanel] = useState<PanelId>(() => (isDesktop ? 'recent' : 'none'));
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [focusedCardId, setFocusedCardId] = useState<string | null>(null);
  const recentBeforeDetail = useRef(false);

  const closeDetail = () => {
    setSelectedGroupId(null);
    if (recentBeforeDetail.current) setOpenPanel('recent');
  };

  const openDetail = (groupId: string) => {
    if (selectedGroupId === groupId) {
      closeDetail();
      return;
    }
    if (selectedGroupId === null) recentBeforeDetail.current = openPanel === 'recent';
    setOpenPanel('none');
    setSelectedGroupId(groupId);
  };

  const togglePanel = (panel: PanelId) => {
    recentBeforeDetail.current = false;
    setSelectedGroupId(null);
    setOpenPanel((current) => (current === panel ? 'none' : panel));
  };

  const sortedStations = [...(stations ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const stationNameById = new Map(sortedStations.map((s) => [s.id ?? 0, s.name ?? `Station ${s.id}`]));

  const orderedIds = navItems.map((item) => item.cardId);

  const focusBy = (direction: 1 | -1) => {
    const nextId = moveFocus(focusedCardId, orderedIds, direction);
    setFocusedCardId(nextId);
    if (nextId) scrollCardIntoView(nextId);
  };

  const activateFocused = () => {
    const item = navItems.find((entry) => entry.cardId === focusedCardId);
    // Focus can briefly point at a card that just left the board (e.g. key-repeat after a bump).
    // Skip — the clamp below resyncs focus to the next card.
    if (!item) return;
    if (item.action) {
      item.action();
      return;
    }
    openDetail(item.groupId);
  };

  // Keep the cursor on a live card as the board changes: stay put if still present, otherwise fall
  // to whatever card slid into the completed card's slot (so Enter → Enter walks the board
  // hands-free). Adjusted during render — the content key means it only acts on a real change.
  const orderedKey = orderedIds.join(' ');
  const [prevOrdered, setPrevOrdered] = useState({ key: orderedKey, ids: orderedIds });
  if (prevOrdered.key !== orderedKey) {
    const next = clampFocus(focusedCardId, prevOrdered.ids, orderedIds);
    setPrevOrdered({ key: orderedKey, ids: orderedIds });
    if (next !== focusedCardId) setFocusedCardId(next);
  }

  // Enter advances the focused card (Start/Done), falling back to its detail when there's no action;
  // Space opens (toggles) the detail, matching click behaviour.
  useKeyboardShortcuts({
    ArrowRight: () => focusBy(1),
    ArrowDown: () => focusBy(1),
    ArrowLeft: () => focusBy(-1),
    ArrowUp: () => focusBy(-1),
    Enter: activateFocused,
    ' ': () => {
      const item = navItems.find((entry) => entry.cardId === focusedCardId);
      if (item) openDetail(item.groupId);
    },
    Escape: () => {
      if (selectedGroupId !== null) {
        closeDetail();
        return;
      }
      if (openPanel === 'recent') setOpenPanel('none');
    },
    r: () => togglePanel('recent'),
  });

  const onSelectStation = (next: StationSelection) => {
    setSelectedGroupId(null);
    setFocusedCardId(null);
    if (next.kind === 'expo') {
      navigate(`/${locationIdParam}/kds/expo`);
      return;
    }
    navigate(`/${locationIdParam}/kds/stations/${next.stationId}/view`);
  };

  const deselectOnOutsideClick = (e: React.MouseEvent) => {
    if (selectedGroupId === null) return;
    if ((e.target as Element).closest('[data-kds-card]')) return;
    closeDetail();
  };

  return {
    // chrome
    sortedStations,
    stationNameById,
    connectionState,
    stationsPending,
    isDesktop,
    // selection / focus state
    selectedGroupId,
    focusedCardId,
    detailOpen: selectedGroupId !== null,
    auxOpen: selectedGroupId === null && openPanel === 'recent',
    // actions
    openDetail,
    closeDetail,
    closeAux: () => setOpenPanel('none'),
    onToggleRecent: () => togglePanel('recent'),
    onSelectStation,
    deselectOnOutsideClick,
  };
}
